import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HexLockup } from '../../../components/FieldCertLogo';
import api from '../../../lib/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../../store/authStore';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { EyeIcon, Spinner } from '../../../components/ui/auth/AuthIcons';

function validatePassword(value) {
  const pw = String(value || '');
  if (pw.length < 12) return 'Password must be at least 12 characters';
  if (!/[A-Z]/.test(pw)) return 'Must include an uppercase letter';
  if (!/[a-z]/.test(pw)) return 'Must include a lowercase letter';
  if (!/[0-9]/.test(pw)) return 'Must include a number';
  return null;
}

export default function ForcePasswordChange() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [shakeBtn, setShakeBtn] = useState(false);
  const { user, clearMustChangePassword } = useAuthStore();
  const navigate = useNavigate();

  const isForcedReset = user?.mustChangePassword === true;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};

    const passwordError = validatePassword(newPassword);
    if (passwordError) errs.newPassword = passwordError;
    if (!confirmPassword) errs.confirmPassword = 'Confirm your password';
    else if (newPassword !== confirmPassword) errs.confirmPassword = 'Passwords do not match';
    if (!isForcedReset && !currentPassword) errs.currentPassword = 'Current password required';

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setShakeBtn(true);
      setTimeout(() => setShakeBtn(false), 500);
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      await api.post('/auth/update-password', {
        oldPassword: isForcedReset ? undefined : currentPassword,
        newPassword,
      });
      clearMustChangePassword();
      toast.success('Password updated. You can now use the app.');
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const ctaClass = [
    'login-cta',
    loading ? 'login-cta--loading' : '',
    shakeBtn ? 'login-cta--shake' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="login-shell login-shell--auth-only">
      <div className="login-shell-bg" aria-hidden="true" />

      <div className="login-auth-panel">
        <div className="login-auth-inner">
          <div className="login-auth-brand">
            <HexLockup iconSize={48} wmSize="sm" gap={12} />
          </div>

          <div className="login-card">
            <h1 className="login-card-heading">Set new password</h1>
            <p className="login-card-lead">
              {isForcedReset
                ? 'Your administrator requires you to set a new secure password before continuing.'
                : 'Change your password below.'}
            </p>

            <form onSubmit={handleSubmit} className="login-form" noValidate>
              {!isForcedReset && (
                <Input
                  label={errors.currentPassword || 'Current password'}
                  error={!!errors.currentPassword}
                  id="current-password"
                  type={showCurrent ? 'text' : 'password'}
                  endAdornment={(
                    <button
                      type="button"
                      className="login-password-toggle"
                      onClick={() => setShowCurrent((v) => !v)}
                      aria-label={showCurrent ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      <EyeIcon open={showCurrent} />
                    </button>
                  )}
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    if (errors.currentPassword) setErrors((prev) => ({ ...prev, currentPassword: null }));
                  }}
                  autoComplete="current-password"
                  placeholder="Your current password"
                  disabled={loading}
                />
              )}

              <Input
                label={errors.newPassword || 'New password'}
                error={!!errors.newPassword}
                id="new-password"
                type={showNew ? 'text' : 'password'}
                endAdornment={(
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowNew((v) => !v)}
                    aria-label={showNew ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <EyeIcon open={showNew} />
                  </button>
                )}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (errors.newPassword) setErrors((prev) => ({ ...prev, newPassword: null }));
                }}
                required
                minLength={12}
                autoComplete="new-password"
                placeholder="12+ chars, upper, lower, number"
                disabled={loading}
              />

              <Input
                label={errors.confirmPassword || 'Confirm new password'}
                error={!!errors.confirmPassword}
                id="confirm-password"
                type={showConfirm ? 'text' : 'password'}
                endAdornment={(
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <EyeIcon open={showConfirm} />
                  </button>
                )}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: null }));
                }}
                required
                minLength={12}
                autoComplete="new-password"
                placeholder="Re-enter new password"
                disabled={loading}
              />

              <Button type="submit" variant="primary" className={ctaClass} disabled={loading}>
                {loading ? <><Spinner /> Updating…</> : 'Update Password'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
